import { supabase, API, authHeaders } from "./supabase";
import { uploadToBucket, audioExt } from "./media";
import { DISPATCH_SELECT } from "../hooks/useDispatches";
import { isAuthError } from "./authState";
import { upsertCrew, findCrew } from "./crew";
import { db } from "./db";

let supportsJobToken = localStorage.getItem("dropit-rpc-jobtoken") !== "no";
let supportsCrew = localStorage.getItem("dropit-rpc-crew") !== "no";

// Resolve the crew contact for this dispatch (upsert new ones) — must run before the RPC so use_count can be bumped.
export const resolveCrew = async (userId, contact) => {
  if (!contact) return { name: null, id: null };
  const name = (contact.contactName || "").trim() || null;
  let id = contact.crewContactId && !String(contact.crewContactId).startsWith("local:") ? contact.crewContactId : null;
  if (!id && (contact.phone || contact.email)) {
    const cached = findCrew(await db.all("crew"), contact);
    const wantSave = contact.saveToCrew !== false && !!name;
    if (cached || wantSave || contact.crewContactId) {
      const row = await upsertCrew(userId, { name: name || cached?.name || "Crew", phone: contact.phone || null, email: contact.email || null, role: cached?.role }, { allowLocal: false }).catch(() => cached);
      if (row && !String(row.id).startsWith("local:")) id = row.id;
      return { name: name || row?.name || null, id };
    }
  }
  return { name, id };
};

export const codeOf = (error) => ["INSUFFICIENT_CREDITS", "WALLET_READ_ONLY", "FAIR_USE_LIMIT", "NOT_AUTHENTICATED", "CANNOT_CANCEL_SENT"].find((c) => error?.message?.includes(c)) || null;

export const extractCard = async ({ audioBlob, photos, text }) => {
  const fd = new FormData();
  if (audioBlob) fd.append("audio", audioBlob, `note.${audioExt(audioBlob)}`);
  (photos || []).forEach((p, i) => fd.append("photos", p.blob || p, `photo-${i}.jpg`));
  if (text?.trim()) fd.append("text", text.trim());
  const r = await fetch(`${API}/ai/extract`, { method: "POST", headers: await authHeaders(), body: fd });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(d.detail || "Extraction failed"), { status: r.status, code: r.status === 401 ? "AUTH" : undefined });
  return d;
};

export async function createDispatchRemote({ user, senderName, card, contact, pin, siteLabel, knownSite, audioBlob, photos, idemKey, jobToken }) {
  let audioPath = null, photoPaths = [];
  if (audioBlob) audioPath = await uploadToBucket("dispatch-audio", user.id, audioBlob, audioExt(audioBlob)).catch(() => null);
  if (photos?.length) photoPaths = (await Promise.all(photos.map((p) => uploadToBucket("dispatch-photos", user.id, p.blob || p, "jpg").catch(() => null)))).filter(Boolean);

  const crew = await resolveCrew(user.id, contact).catch(() => ({ name: contact?.contactName || null, id: null }));
  const crewParams = supportsCrew ? { p_recipient_name: crew.name, p_crew_contact_id: crew.id } : {};
  const params = {
    ...crewParams,
    p_idempotency_key: idemKey,
    p_task_summary: card.tasks?.length ? card.tasks.join("; ") : card.title,
    p_title: card.title, p_tasks: card.tasks || [], p_hazards: card.hazards || [],
    p_transcript: card.transcript || null, p_language: card.language || null,
    p_latitude: pin?.lat ?? null, p_longitude: pin?.lng ?? null,
    p_property_id: knownSite?.id ?? null,
    p_recipient_phone: contact?.phone || null, p_recipient_email: contact?.email || null,
    p_audio_notes_url: audioPath, p_photo_urls: photoPaths.length ? photoPaths : null,
    p_access_code: card.access_code || null, p_access_notes: card.access_notes || null,
    p_sender_name: senderName || null,
  };
  let res = await supabase.rpc("create_dispatch_with_credit", supportsJobToken && jobToken ? { ...params, p_job_token: jobToken } : params);
  if (res.error && supportsCrew && /p_recipient_name|p_crew_contact_id|function .* does not exist|PGRST202/i.test(res.error.message)) {
    supportsCrew = false; localStorage.setItem("dropit-rpc-crew", "no");
    delete params.p_recipient_name; delete params.p_crew_contact_id;
    res = await supabase.rpc("create_dispatch_with_credit", supportsJobToken && jobToken ? { ...params, p_job_token: jobToken } : params);
  }
  if (res.error && supportsJobToken && jobToken && /p_job_token|function .* does not exist|PGRST202/i.test(res.error.message)) {
    supportsJobToken = false; localStorage.setItem("dropit-rpc-jobtoken", "no");
    res = await supabase.rpc("create_dispatch_with_credit", params);
  }
  if (res.error) throw Object.assign(new Error(res.error.message), { code: codeOf(res.error) || (isAuthError(res.error) ? "AUTH" : res.error.code?.startsWith("PGRST") ? "RPC" : null), permanent: !!codeOf(res.error) });
  let dispatch = res.data;

  const siteFields = { access_code_masked: card.access_code || null, access_notes: card.access_notes || null, hazards: card.hazards || [] };
  if (knownSite) {
    const changed = siteFields.access_code_masked !== (knownSite.access_code_masked || null) || siteFields.access_notes !== (knownSite.access_notes || null) || JSON.stringify(siteFields.hazards) !== JSON.stringify(knownSite.hazards || []);
    if (changed) await supabase.from("properties").update(siteFields).eq("id", knownSite.id);
  } else if (pin && !dispatch.property_id) {
    const { data: prop } = await supabase.from("properties").insert({ user_id: user.id, title: siteLabel || card.title, latitude: pin.lat, longitude: pin.lng, ...siteFields }).select().single();
    if (prop) {
      const { data: upd } = await supabase.from("dispatches").update({ property_id: prop.id }).eq("id", dispatch.id).select(DISPATCH_SELECT).single();
      if (upd) dispatch = upd;
    }
  }
  return dispatch;
}
